import { FC } from 'react'
import { Box, CircularProgress, Paper, Typography } from '@mui/material'
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

export interface IChartDataItem {
  name: string
  value: number
  fill: string
}

interface IDashboardChartPanelProps {
  title: string
  subtitle?: string
  data: IChartDataItem[]
  loading?: boolean
  type: 'bar' | 'pie'
  emptyMessage?: string
}

const CHART_HEIGHT = 240
const BAR_CHART_HEIGHT = 280

const ChartTooltip: FC<{ active?: boolean; payload?: Array<{ payload: IChartDataItem }> }> = ({
  active,
  payload,
}) => {
  if (!active || !payload?.length) return null
  const item = payload[0].payload
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        boxShadow: 1,
      }}
    >
      <Typography variant="caption" fontWeight={700}>
        {item.name}: {item.value.toLocaleString('pt-BR')}
      </Typography>
    </Box>
  )
}

export const DashboardChartPanel: FC<IDashboardChartPanelProps> = ({
  title,
  subtitle,
  data,
  loading,
  type,
  emptyMessage = 'Sem dados para exibir.',
}) => {
  const hasData = data.some((item) => item.value > 0)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {subtitle}
        </Typography>
      )}

      <Box sx={{ flex: 1, minHeight: type === 'bar' ? BAR_CHART_HEIGHT : CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? (
          <CircularProgress size={28} />
        ) : !hasData ? (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {emptyMessage}
          </Typography>
        ) : type === 'bar' ? (
          <ResponsiveContainer width="100%" height={BAR_CHART_HEIGHT}>
            <BarChart data={data} margin={{ top: 12, right: 12, left: 4, bottom: 4 }}>
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-25}
                textAnchor="end"
                height={72}
                tickMargin={8}
              />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={48} tickMargin={4} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <PieChart>
              <Pie
                data={data.filter((d) => d.value > 0)}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={78}
                paddingAngle={2}
              >
                {data
                  .filter((d) => d.value > 0)
                  .map((entry) => (
                    <Cell key={entry.name} fill={entry.fill} />
                  ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  )
}
